import axios from "../../../../services/axios";
import { useReducer, useEffect } from "react";
import { School, DbUser } from "../../../../interfaces";
import { Role } from "../../../../enums";
import { AddSingleUserProps } from "../AddDetails/AddSingleUser";
import { formatISO, isAfter, isValid } from "date-fns";
import { logger } from "../../../../services/logger";

export type ClassCreationState = {
  form: {
    schoolId: string;
    teacherId: string;
    courseId: number | null | undefined;
    period: string;
    startDate: Date | null;
    endDate: Date | null;
  };
  data: {
    schools: {
      value: School[];
      succeeded: boolean;
    };
    teachers: {
      value: DbUser[];
      succeeded: boolean;
    };
  };
  currentUser: DbUser;
};

export type UpdateFormAction = {
  type: "updateForm";
  update: Partial<ClassCreationState["form"]>;
};
export type ReplaceStateAction = {
  type: "replaceState";
  state: ClassCreationState;
};
export type DataSuccessAction<T extends keyof ClassCreationState["data"]> = {
  type: "dataSuccess";
  key: T;
  value: ClassCreationState["data"][T]["value"];
};

export type Action =
  | UpdateFormAction
  | ReplaceStateAction
  | DataSuccessAction<keyof ClassCreationState["data"]>;

export const dataSuccess = <T extends keyof ClassCreationState["data"]>({
  key,
  value,
}: Omit<DataSuccessAction<T>, "type">): DataSuccessAction<T> => ({
  type: "dataSuccess",
  key,
  value,
});

export const initClassCreationState = (
  currentUser: DbUser,
  data?: ClassCreationState["data"]
): ClassCreationState => {
  const state: ClassCreationState = {
    form: {
      schoolId: "",
      teacherId: "",
      courseId: 1,
      period: "",
      startDate: null,
      endDate: null,
    },
    data: data ?? {
      schools: {
        value: [],
        succeeded: false,
      },
      teachers: {
        value: [],
        succeeded: false,
      },
    },
    currentUser,
  };
  // Automatically assigns school
  if ([Role.admin, Role.teacher].includes(currentUser.role)) {
    state.form.schoolId = String(currentUser.user_school[0].school_id);
    state.data.schools.value = currentUser.user_school[0].school
      ? [currentUser.user_school[0].school]
      : [];
  }
  // Assigns self to teacher field
  if (currentUser.role === Role.teacher) {
    state.form.teacherId = String(currentUser.id);
    state.data.teachers.value = [currentUser];
  }

  return state;
};

function reducer(state: ClassCreationState, action: Action): ClassCreationState {
  switch (action.type) {
    case "replaceState":
      return action.state;
    case "updateForm": {
      return {
        ...state,
        form: { ...state.form, ...action.update },
      };
    }
    case "dataSuccess": {
      return {
        ...state,
        data: {
          ...state.data,
          [action.key]: {
            value: action.value,
            succeeded: true,
          },
        },
      };
    }
  }
}

export function getError(state: ClassCreationState): string | undefined {
  if (state.data.schools.succeeded && state.data.schools.value.length === 0) {
    return "There are no schools to select. Please contact your administrator to add the school.";
  }
  if (
    [Role.admin, Role.teacher].includes(state.currentUser.role) &&
    !state.currentUser.user_school.length
  ) {
    return "You have not been assigned to a school. Please contact your administrator to assign you to your school.";
  }
  if (
    state.form.schoolId &&
    state.data.teachers.succeeded &&
    state.data.teachers.value.length === 0
  ) {
    return "You do not have any associated teachers for this school. Teachers are required for class creation. Please first create a teacher user.";
  }
  if (state.form.startDate !== null && !isValid(state.form.startDate)) {
    return "The start date is invalid.";
  }
  if (state.form.endDate !== null && !isValid(state.form.endDate)) {
    return "The end date is invalid.";
  }
  if (state.form.startDate !== null && state.form.endDate !== null) {
    if (formatISO(state.form.startDate) === formatISO(state.form.endDate)) {
      return "Start Date and End Date cannot be the same. Please select an earlier Start Date or a later End Date.";
    }
    if (isAfter(state.form.startDate, state.form.endDate)) {
      return "The end date cannot be before start date.";
    }
  }
}

export function useClassCreationState(props: AddSingleUserProps) {
  const [state, dispatch] = useReducer(
    reducer,
    initClassCreationState(props.currentUser)
  );

  useEffect(() => {
    if (!props.open) return;
    if (props.currentUser.role !== Role.super) return;
    const getSchools = async () => {
      try {
        const res = await axios.get<School[]>("/schools");
        dispatch(dataSuccess({ key: "schools", value: res.data }));
      } catch (e) {
        logger.error(e);
      }
    };
    getSchools();
  }, [props.currentUser.role, props.open]);

  useEffect(() => {
    if (!props.open) return;
    // Wait to get teachers until school is chosen
    if (
      !(state.form.schoolId && [Role.super, Role.admin].includes(props.currentUser.role))
    )
      return;
    const getTeachers = async () => {
      try {
        const res = await axios.get<DbUser[]>("/teachers", {
          params: { schoolId: state.form.schoolId },
        });
        dispatch(dataSuccess({ key: "teachers", value: res.data }));
      } catch (e) {
        logger.error(e);
      }
    };
    getTeachers();
  }, [props.currentUser.role, state.form.schoolId, props.open]);

  return [state, dispatch] as const;
}
